import React, { useState } from 'react';
import { toast } from 'sonner';
import { User, Mail, Shield, KeyRound, Eye, EyeOff } from 'lucide-react';

const EMPTY_FORM = { currentPassword: '', newPassword: '', confirmPassword: '' };

function PasswordField({ value, onChange, placeholder, autoComplete }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        type={visible ? 'text' : 'password'}
        required
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="w-full h-10 pl-3 pr-10 rounded-lg border border-slate-200 bg-white text-sm text-slate-800 outline-none focus:border-blue-500"
      />
      <button
        type="button"
        onClick={() => setVisible(v => !v)}
        aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
        title={visible ? 'Ocultar senha' : 'Mostrar senha'}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
      <Icon className="w-4 h-4 text-slate-400 shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] font-medium text-slate-500">{label}</p>
        <p className="text-sm text-slate-800 truncate">{value || '—'}</p>
      </div>
    </div>
  );
}

export default function Profile({ api, user }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const close = () => {
    setEditing(false);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPassword.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/change-password', {
        current_password: form.currentPassword,
        new_password: form.newPassword,
      });
      toast.success('Senha atualizada com sucesso!');
      close();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar a senha');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="profile-page">
      <h1 className="text-2xl font-bold text-slate-900">Meu Perfil</h1>
      <p className="text-sm text-slate-500 mb-6">Informações da sua conta</p>

      <div className="rounded-2xl bg-white shadow-sm overflow-hidden mb-5">
        <div className="flex items-center gap-4 px-6 py-5 bg-gradient-to-r from-blue-50 to-teal-50">
          <div className="w-14 h-14 rounded-full flex items-center justify-center text-white bg-gradient-to-br from-blue-600 to-teal-400">
            <User className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <p className="text-lg font-bold text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500">{user?.profile_name || 'Administrador'}</p>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <InfoRow icon={Mail} label="E-mail" value={user?.email} />
          <InfoRow icon={Shield} label="Tipo de Acesso" value={user?.profile_name || 'Administrador'} />
        </div>
      </div>

      <div className="rounded-2xl bg-white shadow-sm p-6">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="w-4 h-4 text-blue-600" />
          <h2 className="text-base font-semibold text-slate-900">Segurança</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">Gerencie sua senha de acesso</p>

        {!editing ? (
          <>
            <button
              type="button"
              data-testid="profile-change-password-open"
              onClick={() => setEditing(true)}
              className="px-4 h-10 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-teal-500 hover:opacity-90"
            >
              Alterar Senha
            </button>
            <p className="text-xs text-slate-400 mt-3">Sua senha deve ter no mínimo 6 caracteres</p>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="profile-password-form">
            <PasswordField
              autoComplete="current-password"
              placeholder="Senha atual"
              value={form.currentPassword}
              onChange={e => setForm({ ...form, currentPassword: e.target.value })}
            />
            <PasswordField
              autoComplete="new-password"
              placeholder="Nova senha"
              value={form.newPassword}
              onChange={e => setForm({ ...form, newPassword: e.target.value })}
            />
            <PasswordField
              autoComplete="new-password"
              placeholder="Confirmar nova senha"
              value={form.confirmPassword}
              onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
            />
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={saving}
                data-testid="profile-change-password-submit"
                className="px-4 h-10 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-teal-500 hover:opacity-90 disabled:opacity-50"
              >
                {saving ? 'Salvando…' : 'Salvar senha'}
              </button>
              <button
                type="button"
                onClick={close}
                className="px-4 h-10 rounded-lg text-sm font-medium text-slate-600 border border-slate-200 hover:bg-slate-50"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
